import { t as i18nT } from 'i18next';

/**
 * 사전 비용 품의(205) — 비용구분 + 업체별 견적(품목 표) + 지급 총액.
 * 저장 attributes = { content(견적 HTML 스냅샷), data(레거시 APV_SUB.CL02 와 같은 JSON) }.
 * data.amountSummary 는 서버가 details 로 재계산해 덮어씀
 */

export const EXPENSE_PRE_APPROVAL_TYPE = 205;

export const isExpensePreApprovalType = (apvType: number): boolean =>
  apvType === EXPENSE_PRE_APPROVAL_TYPE;

/** 업체 블록 최대 개수 */
export const EXPENSE_VENDOR_MAX = 20;

export const COST_TYPE_OPTIONS = [
  { value: 'cost_type01', label: '이벤트/프로모션' },
  { value: 'cost_type02', label: '운영비용' },
  { value: 'cost_type03', label: 'IT 관련' },
  { value: 'cost_type04', label: '샘플비용' },
  { value: 'cost_type05', label: '소모품구매' },
  { value: 'cost_type06', label: '지급용역비' },
  { value: 'cost_type07', label: '운송/수출부대비용' },
  { value: 'cost_type08', label: '기타비용' },
] as const;
export type CostType = (typeof COST_TYPE_OPTIONS)[number]['value'];
export const DEFAULT_COST_TYPE: CostType = 'cost_type01';
/** 단위(periodType) 입력이 붙는 비용구분 — 지급용역비 */
export const PERIOD_COST_TYPE: CostType = 'cost_type06';

export const PAY_TYPE_OPTIONS = [
  { value: 'pay_type01', label: '계좌이체' },
  { value: 'pay_type02', label: '법인카드' },
  { value: 'pay_type03', label: '구매카드 (어음)' },
  { value: 'pay_type04', label: '기타' },
] as const;
export type PayType = (typeof PAY_TYPE_OPTIONS)[number]['value'];
export const DEFAULT_PAY_TYPE: PayType = 'pay_type01';

export const PERIOD_TYPE_OPTIONS = [
  { value: 'period_type01', label: '컷' },
  { value: 'period_type02', label: '시간' },
  // '일' 은 common 번역(" days")에 덮임 — 단위 전용 키
  { value: 'period_type03', label: '일(단위)' },
  { value: 'period_type04', label: '월' },
  { value: 'period_type05', label: '연' },
] as const;
export type PeriodType = (typeof PERIOD_TYPE_OPTIONS)[number]['value'];
export const DEFAULT_PERIOD_TYPE: PeriodType = 'period_type04';

export const CURRENCY_OPTIONS = [
  { value: 'KRW', label: 'Korean Won' },
  { value: 'JPY', label: 'Japanese Yen' },
  { value: 'CNY', label: 'Chinese Yuan' },
  { value: 'USD', label: 'U.S. Dollar' },
  { value: 'HKD', label: 'Hong Kong Dollar' },
  { value: 'TWD', label: 'New Taiwan Dollar' },
  { value: 'MOP', label: 'Macanese Pataca' },
  { value: 'EUR', label: 'European Euro' },
  { value: 'GBP', label: 'British Pound' },
] as const;
export type Currency = (typeof CURRENCY_OPTIONS)[number]['value'];
export const DEFAULT_CURRENCY: Currency = 'KRW';

const findLabel = (
  options: readonly { value: string; label: string }[],
  value: string,
): string | undefined => options.find((o) => o.value === value)?.label;

const isOneOf = <T extends string>(
  options: readonly { value: T }[],
  value: unknown,
): value is T => options.some((o) => o.value === value);

export const costTypeLabel = (value: string): string =>
  findLabel(COST_TYPE_OPTIONS, value) ?? '';
export const payTypeLabel = (value: string): string =>
  findLabel(PAY_TYPE_OPTIONS, value) ?? '';
export const periodTypeLabel = (value: string): string =>
  findLabel(PERIOD_TYPE_OPTIONS, value) ?? '';

export const sanitizeCurrency = (value: unknown): Currency => {
  const upper = typeof value === 'string' ? value.trim().toUpperCase() : '';
  return isOneOf(CURRENCY_OPTIONS, upper) ? upper : DEFAULT_CURRENCY;
};

/** 작성자 언어 라벨 — 번역 리소스 로드 전이면 i18next 가 빈 값을 돌려줄 수 있어 원문(한국어 키)으로 */
export const localizeLabel = (key: string): string => i18nT(key) || key;

/** 비용구분 라벨 제목 — 레거시와 같이 비용구분이 바뀔 때마다 다시 씀 */
export const buildExpenseSubject = (costType: string): string =>
  `[${localizeLabel(
    costTypeLabel(costType) || costTypeLabel(DEFAULT_COST_TYPE),
  )}]`;

// ===== 폼 상태 =====

/** 입력 중 문자열(콤마 포함)을 그대로 보관 — 저장 직전에만 숫자로 바꿈 */
export type ExpenseDetailRow = {
  id: string;
  itemName: string;
  quantity: string;
  unitPrice: string;
  netAmount: string;
  currency: Currency;
  remark: string;
};

export type ExpenseVendor = {
  id: string;
  vendor: string;
  summary: string;
  payType: PayType;
  periodType: PeriodType;
  details: ExpenseDetailRow[];
};

export type ExpenseAmountSummaryRow = {
  /** 업체명 — 레거시 키 이름 그대로 */
  type: string;
  summary: string;
  amount: number;
  currency: Currency;
};

export type ExpensePreApprovalState = {
  costType: CostType;
  vendors: ExpenseVendor[];
  /** 「지급 총액」 표에 보이는 마지막 계산 결과. null = 아직 계산 전(빈 행 + 0) */
  amountSummary: ExpenseAmountSummaryRow[] | null;
  /** 레거시 isAmountCalculated — 계산 버튼 통과 시 true, 입력칸·선택칸 수정·AI 파일 선택 시 false(업체·행 추가/삭제는 유지) */
  amountCalculated: boolean;
};

let idSeq = 0;
const nextId = (prefix: string) => {
  idSeq += 1;
  return `${prefix}-${Date.now().toString(36)}-${idSeq}`;
};

export const createDetailRow = (
  partial: Partial<Omit<ExpenseDetailRow, 'id'>> = {},
): ExpenseDetailRow => ({
  id: nextId('row'),
  itemName: '',
  quantity: '',
  unitPrice: '',
  netAmount: '',
  currency: DEFAULT_CURRENCY,
  remark: '',
  ...partial,
});

export const createVendor = (
  partial: Partial<Omit<ExpenseVendor, 'id'>> = {},
): ExpenseVendor => ({
  id: nextId('vendor'),
  vendor: '',
  summary: '',
  payType: DEFAULT_PAY_TYPE,
  periodType: DEFAULT_PERIOD_TYPE,
  details: [createDetailRow()],
  ...partial,
});

export const createExpenseState = (): ExpensePreApprovalState => ({
  costType: DEFAULT_COST_TYPE,
  vendors: [createVendor()],
  amountSummary: null,
  amountCalculated: false,
});

// ===== 숫자 입력 =====

/** 레거시 addComma — 정수부만 콤마, 소수부는 그대로 */
const addCommaRaw = (value: number): string => {
  const [intPart, decimal] = String(value).split('.');
  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${
    decimal ? `.${decimal}` : ''
  }`;
};

const addComma = (intPart: string) =>
  intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** 단가·공급가액 — 맨 앞 '-' 허용, 소수 2자리, 정수부 콤마 */
export const formatAmountInput = (raw: string): string => {
  const negative = raw.trim().startsWith('-');
  const cleaned = raw.replace(/[^\d.]/g, '');
  const [intRaw = '', ...rest] = cleaned.split('.');
  const hasDot = cleaned.includes('.');
  const intPart = intRaw.replace(/^0+(?=\d)/, '');
  const decimal = rest.join('').slice(0, 2);
  const body = hasDot
    ? `${addComma(intPart || '0')}.${decimal}`
    : addComma(intPart);
  return `${negative ? '-' : ''}${body}`;
};

/** 수량 — 0 이상 정수, 콤마 */
export const formatQuantityInput = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
  return addComma(digits);
};

/** 콤마 제거 후 숫자. 빈 값은 NaN */
export const parseNumberInput = (value: string): number => {
  const cleaned = value.replace(/,/g, '').trim();
  if (cleaned === '' || cleaned === '-') return NaN;
  return Number(cleaned);
};

const isIntegerCurrency = (currency: string) =>
  currency === 'KRW' || currency === 'JPY';

/** 통화별 표시 — KRW·JPY 정수 반올림, 그 외 소수 2자리 */
export const formatAmount = (amount: number, currency: string): string => {
  if (!Number.isFinite(amount)) return '';
  const fixed = isIntegerCurrency(currency)
    ? String(Math.round(amount))
    : amount.toFixed(2);
  const negative = fixed.startsWith('-');
  const [intPart, decimal] = fixed.replace('-', '').split('.');
  return `${negative ? '-' : ''}${addComma(intPart)}${
    decimal !== undefined ? `.${decimal}` : ''
  }`;
};

const numberOrZero = (value: string) => {
  const n = parseNumberInput(value);
  return Number.isFinite(n) ? n : 0;
};

/** 레거시 저장 제외 조건 — 품목명·수량·단가·공급가액·비고 모두 비면 빈 행 */
export const isEmptyDetailRow = (row: ExpenseDetailRow): boolean =>
  !row.itemName.trim() &&
  !(numberOrZero(row.quantity) > 0) &&
  numberOrZero(row.unitPrice) === 0 &&
  numberOrZero(row.netAmount) === 0 &&
  !row.remark.trim();

// ===== 지급 총액 =====

/** 업체명|적요|통화 단위 합산. 품목명이 있거나 공급가액이 0 아닌 행만 */
export const computeAmountSummary = (
  vendors: ExpenseVendor[],
): ExpenseAmountSummaryRow[] => {
  const map = new Map<string, ExpenseAmountSummaryRow>();
  vendors.forEach((v) => {
    v.details.forEach((row) => {
      const net = numberOrZero(row.netAmount);
      if (!row.itemName.trim() && net === 0) return;
      const type = v.vendor.trim() || '-';
      const summary = v.summary.trim() || '-';
      const key = JSON.stringify([type, summary, row.currency]);
      const prev = map.get(key);
      if (prev) prev.amount += net;
      else map.set(key, { type, summary, amount: net, currency: row.currency });
    });
  });
  return Array.from(map.values());
};

/**
 * 작성 화면 「지급 총액」 표 — 레거시 calculateTotalAmount 그대로.
 * 빈 행 포함 모든 행, 행마다 KRW·JPY 반올림 / 그 외 소수 2자리 후 합산
 */
export const computeDisplaySummary = (
  vendors: ExpenseVendor[],
): ExpenseAmountSummaryRow[] => {
  const map = new Map<string, ExpenseAmountSummaryRow>();
  vendors.forEach((v) => {
    const type = v.vendor.trim() || '-';
    const summary = v.summary.trim() || '-';
    v.details.forEach((row) => {
      const raw = parseNumberInput(row.netAmount);
      let amount = 0;
      if (Number.isFinite(raw)) {
        amount = isIntegerCurrency(row.currency)
          ? Math.round(raw)
          : parseFloat(raw.toFixed(2));
      }
      const key = JSON.stringify([type, summary, row.currency]);
      const prev = map.get(key);
      if (prev) prev.amount += amount;
      else map.set(key, { type, summary, amount, currency: row.currency });
    });
  });
  return Array.from(map.values());
};

export type AmountSummaryGroup = {
  vendor: string;
  rows: ExpenseAmountSummaryRow[];
};

/** 표시용 — 업체명이 같으면 한 그룹(업체명 셀 rowspan) */
export const groupAmountSummary = (
  rows: ExpenseAmountSummaryRow[],
): AmountSummaryGroup[] => {
  const groups: AmountSummaryGroup[] = [];
  rows.forEach((row) => {
    const group = groups.find((g) => g.vendor === row.type);
    if (group) group.rows.push(row);
    else groups.push({ vendor: row.type, rows: [row] });
  });
  return groups;
};

/** 통화별 총 지급액 — 첫 등장 순서 */
export const totalByCurrency = (
  rows: ExpenseAmountSummaryRow[],
): { currency: string; amount: number }[] => {
  const totals: { currency: string; amount: number }[] = [];
  rows.forEach((row) => {
    const t = totals.find((x) => x.currency === row.currency);
    if (t) t.amount += row.amount;
    else totals.push({ currency: row.currency, amount: row.amount });
  });
  return totals;
};

// ===== 검증 =====

/** 위반 위치 — 포커스 이동용 */
export type ExpenseViolation = {
  message: string;
  vendorId?: string;
  rowId?: string;
  field?:
    | 'vendor'
    | 'summary'
    | keyof Omit<ExpenseDetailRow, 'id' | 'currency'>;
};

/** 「지급 총액 계산」 버튼 검사 — 업체 순서대로 첫 위반만 */
export const validateForCalculation = (
  state: ExpensePreApprovalState,
): ExpenseViolation | null => {
  for (const v of state.vendors) {
    if (!v.vendor.trim()) {
      return {
        message: '업체명을 입력해 주세요',
        vendorId: v.id,
        field: 'vendor',
      };
    }
    if (!v.summary.trim()) {
      return {
        message: '헤더적요를 입력해 주세요',
        vendorId: v.id,
        field: 'summary',
      };
    }
    const rows = v.details.filter((r) => !isEmptyDetailRow(r));
    if (rows.length === 0) {
      return {
        message: '품목을 입력해 주세요',
        vendorId: v.id,
        rowId: v.details[0]?.id,
        field: 'itemName',
      };
    }
    for (const r of rows) {
      const at = { vendorId: v.id, rowId: r.id };
      if (!r.itemName.trim()) {
        return { message: '품목명을 입력해 주세요', ...at, field: 'itemName' };
      }
      if (!r.quantity.trim()) {
        return { message: '수량을 입력해 주세요', ...at, field: 'quantity' };
      }
      const qty = parseNumberInput(r.quantity);
      if (!Number.isFinite(qty) || qty < 0) {
        return {
          message: '수량을 올바르게 입력해 주세요',
          ...at,
          field: 'quantity',
        };
      }
      if (!Number.isFinite(parseNumberInput(r.unitPrice))) {
        return {
          message: '단가를 올바르게 입력해 주세요',
          ...at,
          field: 'unitPrice',
        };
      }
      if (!r.netAmount.trim()) {
        return {
          message: '공급가액을 입력해 주세요',
          ...at,
          field: 'netAmount',
        };
      }
      if (!Number.isFinite(parseNumberInput(r.netAmount))) {
        return {
          message: '공급가액을 올바르게 입력해 주세요',
          ...at,
          field: 'netAmount',
        };
      }
    }
  }
  return null;
};

/** 임시저장·상신 공통 검사(레거시 전처리) — 통과면 null */
export const checkExpenseBeforeSave = (
  state: ExpensePreApprovalState | null | undefined,
): string | null => {
  if (!state) return '견적서 내용을 입력해 주세요.';
  const hasValidVendor = state.vendors.some(
    (v) => v.vendor.trim() && v.summary.trim(),
  );
  if (!hasValidVendor) return '견적서 내용을 입력해 주세요.';
  if (!state.amountCalculated) return '지급총액 계산을 먼저 진행해 주세요.';
  // 레거시는 저장 직전 입력값으로 다시 뽑은 amountSummary 로 판정(표 값 아님)
  if (computeAmountSummary(state.vendors).length === 0) {
    return '지급총액 계산 결과가 올바르지 않습니다. 다시 계산해 주세요.';
  }
  return null;
};

// ===== AI 결과 반영 =====

export type ExpenseAiItem = {
  name?: string;
  itemName?: string;
  quantity?: number | string | null;
  unitPrice?: number | string | null;
  netAmount?: number | string | null;
  currency?: string | null;
  remark?: string | null;
};

export type ExpenseAiVendor = {
  vendorName?: string | null;
  description?: string | null;
  paymentMethod?: string | null;
  items?: ExpenseAiItem[] | null;
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseNumberInput(value);
  return NaN;
};

/** AI 지불 방법(한국어 라벨) → 지급방식. 매칭 없으면 계좌이체 */
export const convertPayMethod = (label: unknown): PayType => {
  const text = typeof label === 'string' ? label.replace(/\s/g, '') : '';
  const found = PAY_TYPE_OPTIONS.find(
    (o) => o.label.replace(/\s/g, '') === text,
  );
  return found?.value ?? DEFAULT_PAY_TYPE;
};

const aiItemToRow = (item: ExpenseAiItem): ExpenseDetailRow => {
  const currency = sanitizeCurrency(item.currency);
  const qty = toNumber(item.quantity);
  const price = toNumber(item.unitPrice);
  const net = toNumber(item.netAmount);
  return createDetailRow({
    itemName: item.name ?? item.itemName ?? '',
    quantity: Number.isFinite(qty) ? addCommaRaw(qty) : '',
    unitPrice: Number.isFinite(price) ? formatAmount(price, currency) : '',
    netAmount: Number.isFinite(net) ? formatAmount(net, currency) : '',
    currency,
    remark: item.remark ?? '',
  });
};

const aiVendorToVendor = (data: ExpenseAiVendor): ExpenseVendor => {
  const rows = (data.items ?? []).map(aiItemToRow);
  return createVendor({
    vendor: data.vendorName ?? '',
    summary: data.description ?? '',
    payType: convertPayMethod(data.paymentMethod),
    details: rows.length ? rows : [createDetailRow()],
  });
};

/** 레거시 isItemEmpty — 업체명·적요·표 입력 중 하나라도 있거나 행이 2개 이상이면 비어있지 않음 */
const isVendorEmpty = (v: ExpenseVendor) =>
  !v.vendor &&
  !v.summary &&
  v.details.length <= 1 &&
  v.details.every(
    (r) =>
      !r.itemName.trim() &&
      !r.quantity.trim() &&
      !r.unitPrice.trim() &&
      !r.netAmount.trim() &&
      !r.remark.trim(),
  );

/** AI 결과 1건 = 업체 1블록. 첫 블록이 비어 있으면 첫 결과로 채우고 나머지는 뒤에 추가(개수 제한 없음 — 레거시 동일) */
export const applyAiVendors = (
  state: ExpensePreApprovalState,
  results: ExpenseAiVendor[],
): ExpensePreApprovalState => {
  const incoming = results.filter(Boolean).map(aiVendorToVendor);
  if (incoming.length === 0) return state;
  const [first, ...others] = state.vendors;
  const vendors =
    first && isVendorEmpty(first)
      ? [incoming[0], ...others, ...incoming.slice(1)]
      : [...state.vendors, ...incoming];
  return { ...state, vendors };
};

// ===== 저장 =====

type Labeled<T extends string = string> = { value: T; label: string };

export type ExpenseDetailData = {
  itemName: string;
  quantity: number;
  unitPrice: number;
  netAmount: number;
  currency: string;
  remark: string;
};

export type ExpenseVendorData = {
  vendor: Labeled;
  summary: Labeled;
  payType: Labeled;
  periodType: Labeled;
  details: ExpenseDetailData[];
};

export type ExpensePreApprovalData = {
  classification_opt: Labeled;
  subject?: string | null;
  items: ExpenseVendorData[];
  amountSummary?: ExpenseAmountSummaryRow[];
};

export type ExpensePreApprovalAttributes = {
  content: string;
  data: ExpensePreApprovalData;
};

const toDetailData = (row: ExpenseDetailRow): ExpenseDetailData => ({
  itemName: row.itemName.trim(),
  quantity: numberOrZero(row.quantity),
  unitPrice: numberOrZero(row.unitPrice),
  netAmount: numberOrZero(row.netAmount),
  currency: row.currency,
  remark: row.remark.trim(),
});

export const buildExpenseData = (
  state: ExpensePreApprovalState,
  subject: string,
): ExpensePreApprovalData => ({
  classification_opt: {
    value: state.costType,
    label: localizeLabel(costTypeLabel(state.costType)),
  },
  subject,
  items: state.vendors.map((v) => ({
    vendor: { value: v.vendor.trim(), label: v.vendor.trim() },
    summary: { value: v.summary.trim(), label: v.summary.trim() },
    // label 은 value 에서 다시 만듦 — 레거시는 통화 option 텍스트가 섞여 저장되는 버그가 있음
    payType: {
      value: v.payType,
      label: localizeLabel(payTypeLabel(v.payType)),
    },
    periodType: {
      value: v.periodType,
      label: localizeLabel(periodTypeLabel(v.periodType)),
    },
    details: v.details.filter((r) => !isEmptyDetailRow(r)).map(toDetailData),
  })),
  amountSummary: computeAmountSummary(state.vendors),
});

// ===== 복원(편집·조회) =====

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const labeledValue = (value: unknown): string => {
  const rec = asRecord(value);
  if (!rec) return typeof value === 'string' ? value : '';
  const v = rec.value ?? rec.label;
  return typeof v === 'string' ? v : '';
};

const labeledLabel = (value: unknown): string => {
  const rec = asRecord(value);
  const l = rec?.label;
  return typeof l === 'string' ? l : '';
};

/** attributes.data — 객체 또는 레거시 JSON 문자열 */
export const parseExpenseData = (
  attributes: unknown,
): ExpensePreApprovalData | null => {
  const raw = asRecord(attributes)?.data;
  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  const rec = asRecord(data);
  if (!rec) return null;
  const items = Array.isArray(rec.items) ? rec.items : [];
  return {
    classification_opt: {
      value: labeledValue(rec.classification_opt),
      label: labeledLabel(rec.classification_opt),
    },
    subject: typeof rec.subject === 'string' ? rec.subject : null,
    items: items.map((item) => {
      const it = asRecord(item) ?? {};
      const details = Array.isArray(it.details) ? it.details : [];
      return {
        vendor: {
          value: labeledValue(it.vendor),
          label: labeledLabel(it.vendor),
        },
        summary: {
          value: labeledValue(it.summary),
          label: labeledLabel(it.summary),
        },
        payType: {
          value: labeledValue(it.payType),
          label: labeledLabel(it.payType),
        },
        periodType: {
          value: labeledValue(it.periodType),
          label: labeledLabel(it.periodType),
        },
        details: details.map((d) => {
          const row = asRecord(d) ?? {};
          const num = (v: unknown) => {
            const n = toNumber(v);
            return Number.isFinite(n) ? n : 0;
          };
          return {
            itemName: typeof row.itemName === 'string' ? row.itemName : '',
            quantity: num(row.quantity),
            unitPrice: num(row.unitPrice),
            netAmount: num(row.netAmount),
            currency: sanitizeCurrency(row.currency),
            remark: typeof row.remark === 'string' ? row.remark : '',
          };
        }),
      };
    }),
    amountSummary: Array.isArray(rec.amountSummary)
      ? rec.amountSummary.map((s) => {
          const row = asRecord(s) ?? {};
          const amount = toNumber(row.amount);
          return {
            type: typeof row.type === 'string' ? row.type : '-',
            summary: typeof row.summary === 'string' ? row.summary : '-',
            amount: Number.isFinite(amount) ? amount : 0,
            currency: sanitizeCurrency(row.currency),
          };
        })
      : undefined,
  };
};

/**
 * 편집 복원 금액 — 레거시처럼 통화 형식으로 채우되, KRW·JPY 소수는 반올림하지 않음
 * (반올림해 채우면 다시 저장할 때 금액이 바뀜)
 */
const restoreAmountInput = (amount: number, currency: string): string =>
  isIntegerCurrency(currency) && !Number.isInteger(amount)
    ? addCommaRaw(amount)
    : formatAmount(amount, currency);

/** 편집 진입 — 저장 data → 폼 상태. 지급 총액은 저장값 기준으로 계산된 상태로 시작(레거시 동일) */
export const expenseDataToState = (
  data: ExpensePreApprovalData,
): ExpensePreApprovalState => {
  const vendors = data.items.map((item) =>
    createVendor({
      vendor: item.vendor.value,
      summary: item.summary.value,
      payType: isOneOf(PAY_TYPE_OPTIONS, item.payType.value)
        ? item.payType.value
        : DEFAULT_PAY_TYPE,
      periodType: isOneOf(PERIOD_TYPE_OPTIONS, item.periodType.value)
        ? item.periodType.value
        : DEFAULT_PERIOD_TYPE,
      details: item.details.length
        ? item.details.map((d) => {
            const currency = sanitizeCurrency(d.currency);
            return createDetailRow({
              itemName: d.itemName,
              quantity: addCommaRaw(d.quantity),
              unitPrice: restoreAmountInput(d.unitPrice, currency),
              netAmount: restoreAmountInput(d.netAmount, currency),
              currency,
              remark: d.remark,
            });
          })
        : [createDetailRow()],
    }),
  );
  const safeVendors = vendors.length ? vendors : [createVendor()];
  return {
    costType: isOneOf(COST_TYPE_OPTIONS, data.classification_opt.value)
      ? data.classification_opt.value
      : DEFAULT_COST_TYPE,
    vendors: safeVendors,
    // 레거시 편집 진입 — 복원 직후 계산을 돌리고 계산 완료 상태로 시작
    amountSummary: computeDisplaySummary(safeVendors),
    amountCalculated: true,
  };
};
